import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { FindProductStocksResponseDto } from './dtos/find-product-stocks.response.dto';
import { BaseGatewayService } from '../ws/base-gateway.service';
import { PosOfflineDbService } from '../shared/offline/pos-offline-db.service';
import { OfflineService } from '../shared/offline/offline.service';

// StoreStock meaning:
// - undefined → stock data not yet loaded
// - null      → stock is unlimited
// - number    → finite stock quantity
export type StoreStock = number | null | undefined;

@Injectable({ providedIn: 'root' })
export class StockLayerGatewayService extends BaseGatewayService {
  protected override namespace = 'stock-layers';
  private productStockMap = new Map<number, WritableSignal<StoreStock>>();
  private offlineDbService = inject(PosOfflineDbService);
  private offlineService = inject(OfflineService);
  private currentStoreId: number | null = null;

  override disconnect() {
    this.productStockMap.clear();
    super.disconnect();
  }

  subscribeToProductStocks(storeId: number) {
    this.currentStoreId = storeId;
    this.loadCachedStocks(storeId);

    if (this.offlineService.isOffline()) {
      return;
    }

    if (!this.socket?.connected) {
      this.connect();
    }

    const socket = this.socket;
    if (!socket) return;

    if (storeId == null) return;

    socket.off('productStocks');

    socket.emit('subscribeToProductStocks', { storeId });

    socket.on('productStocks', (data: FindProductStocksResponseDto) => {
      for (const o of data) {
        if (!this.productStockMap.has(o.productId)) {
          this.productStockMap.set(o.productId, signal(o.stock));
        } else {
          this.productStockMap.get(o.productId)!.set(o.stock);
        }
      }

      this.cacheStocks(storeId, data);
    });
  }

  private async loadCachedStocks(storeId: number): Promise<void> {
    try {
      const cachedStocks = await this.offlineDbService.getAllProductStocks(storeId);

      cachedStocks.forEach((stock, productId) => {
        if (!this.productStockMap.has(productId)) {
          this.productStockMap.set(productId, signal(stock));
        } else {
          this.productStockMap.get(productId)!.set(stock);
        }
      });
    } catch (error) {
      console.error('[Stock Gateway] Failed to load cached stocks:', error);
    }
  }

  private async cacheStocks(storeId: number, data: FindProductStocksResponseDto): Promise<void> {
    try {
      await this.offlineDbService.saveProductStocks(storeId, data);
    } catch (error) {
      console.error('[Stock Gateway] Failed to cache stocks:', error);
    }
  }

  hasProductStock = (productId: number): Signal<boolean> => {
    const stockSignal = this.getProductStock(productId);
    return computed(() => {
      const stock = stockSignal();
      if (this.offlineService.isOffline() && stock === undefined) {
        return true;
      }
      return stock !== undefined && (stock === null || stock > 0); // null = unlimited
    });
  };

  getProductStock = (productId: number): WritableSignal<StoreStock> => {
    if (!this.productStockMap.has(productId)) {
      this.productStockMap.set(productId, signal(undefined));
    }
    return this.productStockMap.get(productId)!;
  };

  /**
   * Update product stock after a transaction (for offline mode)
   */
  updateProductStock(productId: number, newStock: number | null): void {
    if (!this.productStockMap.has(productId)) {
      this.productStockMap.set(productId, signal(newStock));
    } else {
      this.productStockMap.get(productId)!.set(newStock);
    }
  }

  /**
   * Refresh stocks from cache (useful after transaction)
   */
  async refreshCachedStocks(): Promise<void> {
    if (this.currentStoreId) {
      await this.loadCachedStocks(this.currentStoreId);
    }
  }
}
